import { useDispatch, useSelector } from 'react-redux';
import { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { cookieShareDomain } from '../services/singnin.service';

import Snackbar from '@mui/material/Snackbar';

import Login from '../pages/Login';
import OneTimePassword from '../pages/OneTimePassword';
import LoginMethod from '../pages/LoginMethod';
import UploadTemporaryDocument from '../pages/UploadTemporaryDocument';
import Journey from '../pages/Journey';
import Anamnese from '../pages/Anamnese';
import HomeAnamnese from '../pages/HomeAnamnese';
import FinishAnamnese from '../pages/FinishAnamnese';

import RatingNotification from '../components/shared/RatingNotification';
import ModalFileUpload from '../components/shared/ModalFileUpload';
import RecommendationModal from '../components/shared/RecommendationModal';
import { customEventTracking, pageview } from '../lib/gtm';

import { RootState } from '../store';
import { AuthContext, useAuth } from '../contexts/AuthContext';
import { expireAuth, saveOyanaAuth } from '../features/singin/singin-slice';
import { hideNotification, successNotification } from '../features/notifications/notifications-slice';
import Address from '../pages/Address';
import CancelReasonFeedback from '../pages/CancelReasonFeedback';
import ConfirmationSignalSchedule from '../pages/ConfirmationSignalSchedule';
import { useOyana } from '../shared/hooks/useOyana';
import NotFound from '../pages/NotFound';
import AttendanceDeclaration from '../pages/AttendanceDeclaration';
import CancelSignalSchedule from '../pages/CancelSignalSchedule';
import ConfirmationSignalScheduleDeprecated from '../pages/ConfirmationSignalScheduleDeprecated';
import ConfirmationSchedule from '../pages/ConfirmationSchedule';
import HomeV2 from '../pages/HomeV2';

//* Rota para preview do PDF, pode ser usada para desenvolvimento e testes
// import { PDFPreview } from '../components/shared/PDFGenerator/preview';

const AuthProvider = function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const [cookies] = useCookies(['oya_oyana_access_token']);

  dispatch(saveOyanaAuth(cookies['oya_oyana_access_token'] ? cookies['oya_oyana_access_token'] : ''));

  return (
    <AuthContext.Provider
      value={{
        token: cookies['oya_oyana_access_token']
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const RequireAuth = function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const App = () => {
  const location = useLocation();
  const [cookies, setCookie, removeCookie] = useCookies(['oya_oyana_access_token']);
  const navigate = useNavigate();
  const {
    notifications: { message, open },
    singIn
  } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const oyana = useOyana();

  useEffect(() => {
    const status = searchParams.get('status');

    // Google Analytics custom events
    customEventTracking.send({ event: 'journey_init' });

    if (status === 'paid') {
      dispatch(successNotification({ message: 'Pagamento realizado com sucesso!', open: true }));
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    // Google Analytics
    pageview(location.pathname);
    //Scroll to top
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (singIn.auth.expiredToken) {
      removeCookie('oya_oyana_access_token', { domain: cookieShareDomain });
      dispatch(expireAuth(false));
      navigate('/login');
    }
  }, [singIn.auth.expiredToken]);

  return (
    <>
      <Routes>
        <Route index path="/login" element={<Login />} />
      </Routes>
      <Routes>
        <Route index path="/login/metodo-de-envio" element={<LoginMethod />} />
      </Routes>
      <Routes>
        <Route path="/login/codigo" element={<OneTimePassword />} />
      </Routes>
      <Routes>
        <Route path="/pagina-nao-encontrada" element={<NotFound />} />
      </Routes>
      <Routes>
        <Route path="/declaracao-de-comparecimento" element={<AttendanceDeclaration />} />
      </Routes>
      <AuthProvider>
        <Routes>
          <Route path="/arquivo-temporario/:journeyId/oyana/:oyanaId" element={<UploadTemporaryDocument />} />
          <Route path="/home-anamnese/:formId/:journeyId/:oyanaId?" element={<HomeAnamnese />} />
          <Route
            path="/responda-anamnese/:formId/:journeyId/:oyanaId?"
            element={
              <Suspense fallback={<div>Carregando...</div>}>
                <Anamnese />
              </Suspense>
            }
          />
          <Route path="/fim-anamnese" element={<FinishAnamnese />} />
          <Route path="/atualizar-dados/:formId/:journeyId/:oyanaId?" element={<Address />} />
          <Route path="/confirmar-consulta/:scheduleId/" element={<ConfirmationSignalSchedule />} />
          <Route path="/reagendar-consulta/:scheduleId/" element={<CancelSignalSchedule />} />
          {/**
           * @deprecated Rotas sinal-de-agendamento e confirmar-agendamento estão sendo substituídas
           * por reagendar-consulta e confirmar-consulta, respectivamente.
           * As rotas antigas permanecerão ativas por um período de transição, mas serão removidas em versões futuras.
           * Por favor, atualize seus links e referências para usar as novas rotas.
           */}
          <Route path="/sinal-de-agendamento/:scheduleId/" element={<ConfirmationSignalScheduleDeprecated />} />
          <Route path="/confirmar-agendamento/:scheduleId/" element={<ConfirmationSchedule />} />
          <Route path="/motivo-do-cancelamento/:scheduleId" element={<CancelReasonFeedback />} />
          <Route
            path="/jornada/:journeyid"
            element={
              <RequireAuth>
                <Journey />
              </RequireAuth>
            }
          />
          <Route path="/historico" element={<Navigate to="/" replace />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <HomeV2 />
              </RequireAuth>
            }
          >
            <Route index element={null} />
            <Route path="meus-arquivos" element={null} />
            <Route path="meus-arquivos/:category" element={null} />
            {/* <Route path="meus-arquivos/declaracao-de-comparecimento/solicitar" element={null} /> */}
            <Route path="meus-exames" element={null} />
            <Route path="meus-exames/:category" element={null} />
          </Route>
        </Routes>
      </AuthProvider>
      {/*
        * Rota para preview do PDF, pode ser usada para desenvolvimento e testes
        <Routes>
          <Route index path="/pdf-preview" element={<PDFPreview />} />
        </Routes> 
      */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={open}
        onClose={() => dispatch(hideNotification())}
        message={message}
        autoHideDuration={6000}
      />
      <RatingNotification />
      <RecommendationModal />
      <ModalFileUpload />
    </>
  );
};

export default App;
